// src/context/NotificationContext.jsx
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { notificationService } from '../apis/notificationService';
import { connectWebSocket, authenticateWebSocket, setWebSocketReconnectAuth } from '../services/websocketService';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const userId = currentUser?.userId || currentUser?.id;

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const listenersAttached = useRef(false);
  const socketRef = useRef(null);

  // ---- Initial REST load ----
  useEffect(() => {
    if (!userId) return;
    const loadInitialNotifications = async () => {
      try {
        const response = await notificationService.getNotifications({ limit: 50 });
        if (response.success) {
          setNotifications(response.data?.notifications || []);
          const unread = response.data?.notifications.filter(n => !n.isRead).length;
          setUnreadCount(unread);
        }
      } catch (error) {
        console.error('Failed to load initial notifications:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInitialNotifications();
  }, [userId]);

  // ---- WebSocket setup ----
  useEffect(() => {
    if (!userId || listenersAttached.current) return;

    const reAuthenticate = () => {
      const s = socketRef.current;
      if (s?.connected) {
        authenticateWebSocket(userId);
      }
    };

    setWebSocketReconnectAuth(reAuthenticate);

    // Connect with credentials (cookies will be sent automatically)
    const socket = connectWebSocket();
    socketRef.current = socket;

    const handleConnect = () => {
      setIsConnected(true);
      reAuthenticate();
    };

    const handleDisconnect = () => {
      setIsConnected(false);
    };

    const handleAuthenticated = () => {
      setIsConnected(true);
    };

    const handleAuthError = (error) => {
      console.error('❌ WebSocket authentication error', error);
      setIsConnected(false);
      // Retry after a delay (no token to refresh)
      setTimeout(reAuthenticate, 5000);
    };

    const handleNewNotification = (notification) => {
      setNotifications(prev => [notification, ...prev]);
      if (!notification.isRead) {
        setUnreadCount(prev => prev + 1);
      }
      if (Notification.permission === 'granted') {
        new Notification(notification.title, { body: notification.message });
      }
    };

    const handleUnreadCountUpdate = (data) => {
      setUnreadCount(data.unreadCount);
    };

    const handleStatusChanged = (data) => {
      if (data.notificationId === 'all') {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
      } else {
        setNotifications(prev =>
          prev.map(n =>
            n.notificationId === data.notificationId
              ? { ...n, isRead: data.isRead, readAt: data.readAt }
              : n
          )
        );
        setUnreadCount(prev => (data.isRead ? Math.max(0, prev - 1) : prev + 1));
      }
    };

    const handleDeleted = (data) => {
      setNotifications(prev => prev.filter(n => n.notificationId !== data.notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('authenticated', handleAuthenticated);
    socket.on('authentication_error', handleAuthError);
    socket.on('new_notification', handleNewNotification);
    socket.on('unread_count_update', handleUnreadCountUpdate);
    socket.on('notification_status_changed', handleStatusChanged);
    socket.on('notification_deleted', handleDeleted);

    listenersAttached.current = true;

    if (socket.connected) {
      reAuthenticate();
    }

    return () => {
      setWebSocketReconnectAuth(null);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('authenticated', handleAuthenticated);
      socket.off('authentication_error', handleAuthError);
      socket.off('new_notification', handleNewNotification);
      socket.off('unread_count_update', handleUnreadCountUpdate);
      socket.off('notification_status_changed', handleStatusChanged);
      socket.off('notification_deleted', handleDeleted);
      listenersAttached.current = false;
      socketRef.current = null;
    };
  }, [userId]); // No token dependency

  // ---- Actions (unchanged) ----
  const markAsRead = useCallback(async (notificationId) => {
    try {
      setNotifications(prev =>
        prev.map(n =>
          n.notificationId === notificationId ? { ...n, isRead: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      await notificationService.markAsRead(notificationId);
    } catch (error) {
      setNotifications(prev =>
        prev.map(n =>
          n.notificationId === notificationId ? { ...n, isRead: false } : n
        )
      );
      setUnreadCount(prev => prev + 1);
      console.error('markAsRead failed', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      await notificationService.markAllAsRead();
    } catch (error) {
      console.error('markAllAsRead failed', error);
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      const isUnread = notifications.find(n => n.notificationId === notificationId)?.isRead;
      setNotifications(prev => prev.filter(n => n.notificationId !== notificationId));
      if (!isUnread) setUnreadCount(prev => Math.max(0, prev - 1));
      await notificationService.deleteNotification(notificationId);
    } catch (error) {
      console.error('deleteNotification failed', error);
    }
  }, [notifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    isConnected,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};